import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Menu, Button, Tooltip, Modal, Input, Spin, Tag, Space } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined, UserOutlined, CarOutlined, FileTextOutlined,
  FileProtectOutlined, WifiOutlined, SafetyCertificateOutlined,
  TeamOutlined, FileExcelOutlined, AuditOutlined, SettingOutlined,
  LogoutOutlined, ThunderboltOutlined, BarChartOutlined, CalendarOutlined,
  MobileOutlined, SearchOutlined, AppstoreOutlined, EnvironmentOutlined, SendOutlined,
} from '@ant-design/icons';
import { isAdmin, isTecnico, clearSession } from '../utils/auth';
import axiosInstance from '../utils/axios';
import TelegramVinculo from './TelegramVinculo';

const { Sider, Content } = Layout;

const SIDEBAR_BG = '#1b2a40';
const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED = 64;

const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = isAdmin();
  const tecnico = isTecnico();

  const [collapsed, setCollapsed] = useState(
    localStorage.getItem('sidebarCollapsed') === 'true'
  );

  const [progressKey, setProgressKey] = useState(0);
  const prevPath = useRef(location.pathname);

  const [buscarOpen, setBuscarOpen] = useState(false);
  const [buscarQ, setBuscarQ] = useState('');
  const [buscarResults, setBuscarResults] = useState(null);
  const [buscarLoading, setBuscarLoading] = useState(false);
  const [telegramOpen, setTelegramOpen] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setBuscarResults(null); return; }
    setBuscarLoading(true);
    try {
      const r = await axiosInstance.get(`/buscar?q=${encodeURIComponent(q)}`);
      setBuscarResults(r.data);
    } catch {} finally { setBuscarLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(buscarQ), 350);
    return () => clearTimeout(t);
  }, [buscarQ, doSearch]);

  const irA = (path) => { setBuscarOpen(false); setBuscarQ(''); setBuscarResults(null); navigate(path); };

  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setBuscarOpen(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setProgressKey(k => k + 1);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
  };

  const cerrarSesion = () => {
    clearSession();
    navigate('/');
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/clientes/')) return '/clientes';
    if (path.startsWith('/vehiculos/')) return '/vehiculos';
    if (path.startsWith('/accesorios/')) return '/accesorios';
    return path;
  };


  const menuItems = tecnico ? [
    {
      key: '/dashboard',
      icon: <HomeOutlined />,
      label: 'Inicio',
    },
    {
      type: 'group',
      label: 'Operaciones',
      children: [
        { key: '/mapa',      icon: <EnvironmentOutlined />, label: 'Mapa GPS' },
        { key: '/clientes',  icon: <UserOutlined />,        label: 'Clientes' },
        { key: '/vehiculos', icon: <CarOutlined />,         label: 'Vehículos' },
        { key: '/ordenes',   icon: <FileTextOutlined />,    label: 'Órdenes' },
      ],
    },
  ] : [
    {
      key: '/dashboard',
      icon: <HomeOutlined />,
      label: 'Inicio',
    },
    {
      type: 'group',
      label: 'Operaciones',
      children: [
        { key: '/agenda',            icon: <CalendarOutlined />,        label: 'Agenda' },
        { key: '/clientes',          icon: <UserOutlined />,            label: 'Clientes' },
        { key: '/vehiculos',         icon: <CarOutlined />,             label: 'Vehículos' },
        { key: '/ordenes',           icon: <FileTextOutlined />,        label: 'Órdenes' },
        { key: '/contratos',         icon: <FileProtectOutlined />,     label: 'Contratos' },
        { key: '/mapa',              icon: <EnvironmentOutlined />,     label: 'Mapa GPS' },
        { key: '/gps',               icon: <WifiOutlined />,            label: 'GPS' },
        { key: '/lineas',            icon: <MobileOutlined />,          label: 'SIM Cards' },
        { key: '/vision',            icon: <SafetyCertificateOutlined />, label: 'HSS Vision' },
        { key: '/usuarios-clientes', icon: <TeamOutlined />,            label: 'Users / Pass' },
        { key: '/accesorios',        icon: <AppstoreOutlined />,        label: 'Inventario' },
      ],
    },
    {
      type: 'group',
      label: 'Reportes',
      children: [
        { key: '/operativo',          icon: <ThunderboltOutlined />, label: 'Dashboard Operativo' },
        ...(admin ? [{ key: '/dashboard-gerencial', icon: <BarChartOutlined />, label: 'Gerencial' }] : []),
        { key: '/reportes',           icon: <FileExcelOutlined />,   label: 'Reportes' },
      ],
    },
    ...(admin ? [{
      type: 'group',
      label: 'Administración',
      children: [
        { key: '/audit-log', icon: <AuditOutlined />,   label: 'Registro de Actividad' },
        { key: '/settings',  icon: <SettingOutlined />, label: 'Configuración' },
      ],
    }] : []),
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <style>{`
        @keyframes hss-progress {
          0%   { width: 0%;   opacity: 1; }
          75%  { width: 88%;  opacity: 1; }
          100% { width: 100%; opacity: 0; }
        }
        .hss-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: #3b5998;
          z-index: 10000;
          border-radius: 0 2px 2px 0;
          pointer-events: none;
          animation: hss-progress 0.55s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        @keyframes hss-page-enter {
          from { opacity: 0; transform: translateY(7px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hss-page-enter {
          animation: hss-page-enter 0.22s ease-out;
        }

        .hss-sider .ant-menu-item {
          height: 32px !important;
          line-height: 32px !important;
          margin-block: 1px !important;
          font-size: 13px !important;
          font-weight: 600 !important;
        }
        .hss-sider .ant-menu-item .ant-menu-item-icon {
          font-size: 13px !important;
        }
        .hss-sider .ant-menu-item-group-title {
          padding-top: 8px !important;
          padding-bottom: 2px !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          letter-spacing: 0.8px !important;
        }
        .hss-sider .ant-menu-root {
          padding-top: 4px !important;
        }
        .hss-sider {
          overflow: visible !important;
        }
        .hss-sider .ant-layout-sider-children {
          display: flex !important;
          flex-direction: column !important;
          height: 100% !important;
          overflow: hidden !important;
        }
        .hss-sider .hss-menu-scroll {
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }
      `}</style>
      {progressKey > 0 && <div key={progressKey} className="hss-progress-bar" />}

      <Sider
        collapsed={collapsed}
        width={SIDEBAR_WIDTH}
        collapsedWidth={SIDEBAR_COLLAPSED}
        className="hss-sider"
        style={{
          background: SIDEBAR_BG,
          position: 'fixed',
          inset: '0 auto 0 0',
          zIndex: 200,
        }}
      >
        {/* Botón colapsar — pestaña en el borde derecho */}
        <Tooltip title={collapsed ? 'Expandir menú' : 'Colapsar menú'} placement="right">
          <button
            onClick={toggle}
            style={{
              position: 'absolute',
              top: '50%',
              right: -14,
              transform: 'translateY(-50%)',
              zIndex: 300,
              width: 14,
              height: 48,
              background: SIDEBAR_BG,
              border: 'none',
              borderRadius: '0 6px 6px 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.6)',
              fontSize: 10,
              padding: 0,
              boxShadow: '2px 0 6px rgba(0,0,0,0.2)',
            }}
          >
            {collapsed ? '▶' : '◀'}
          </button>
        </Tooltip>

        {/* Buscador */}
        <div style={{ padding: '8px 10px 4px', flexShrink: 0 }}>
          <Tooltip title="Búsqueda global (Ctrl+K)" placement="right">
            <Button
              icon={<SearchOutlined />}
              onClick={() => setBuscarOpen(true)}
              size="small"
              style={{
                width: collapsed ? 32 : '100%',
                borderRadius: 6,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              {!collapsed && <span style={{ fontSize: 12, opacity: 0.8 }}>Buscar...</span>}
            </Button>
          </Tooltip>
        </div>

        {/* Menu */}
        <div className="hss-menu-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[getSelectedKey()]}
            items={menuItems}
            style={{ background: 'transparent', border: 'none', paddingTop: 8 }}
            onClick={({ key }) => navigate(key)}
          />
        </div>

        {/* Footer: Telegram y salir */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px', flexShrink: 0 }}>
          <Tooltip title="Vincular Telegram" placement="right">
            <Button
              icon={<SendOutlined />}
              size="small"
              onClick={() => setTelegramOpen(true)}
              style={{ borderRadius: 6, width: collapsed ? 32 : '100%', marginBottom: 6 }}
            >
              {!collapsed && 'Telegram'}
            </Button>
          </Tooltip>
          <Tooltip title="Cerrar sesión" placement="right">
            <Button
              icon={<LogoutOutlined />}
              size="small"
              type="primary"
              danger
              onClick={cerrarSesion}
              style={{ borderRadius: 6, width: collapsed ? 32 : '100%' }}
            >
              {!collapsed && 'Salir'}
            </Button>
          </Tooltip>
        </div>
      </Sider>
      <TelegramVinculo open={telegramOpen} onClose={() => setTelegramOpen(false)} />

      <Content style={{
        marginLeft: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH,
        transition: 'margin-left 0.2s',
        minHeight: '100vh',
      }}>
        <div key={location.pathname} className="hss-page-enter">
          {children}
        </div>
      </Content>

      {/* Modal búsqueda global */}
      <Modal
        open={buscarOpen}
        onCancel={() => { setBuscarOpen(false); setBuscarQ(''); setBuscarResults(null); }}
        footer={null}
        title={<Space><SearchOutlined style={{ color: '#3b5998' }} />Búsqueda global</Space>}
        width={560}
      >
        <Input
          autoFocus
          placeholder="Buscar cliente, vehículo, GPS, orden..."
          value={buscarQ}
          onChange={e => setBuscarQ(e.target.value)}
          size="large"
          prefix={buscarLoading ? <Spin size="small" /> : <SearchOutlined style={{ color: '#aaa' }} />}
          style={{ borderRadius: 8, marginBottom: 16 }}
        />
        {buscarResults && (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {buscarResults.clientes?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: 1, marginBottom: 4 }}>CLIENTES</div>
                {buscarResults.clientes.map(c => (
                  <div key={c.dni} onClick={() => irA(`/clientes/${c.dni}`)}
                    style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontWeight: 600 }}>{c.nombre}</span>
                    <Tag style={{ fontFamily: 'monospace' }}>{c.dni}</Tag>
                  </div>
                ))}
              </div>
            )}
            {buscarResults.vehiculos?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: 1, marginBottom: 4 }}>VEHÍCULOS</div>
                {buscarResults.vehiculos.map(v => (
                  <div key={v.chapa} onClick={() => irA(`/vehiculos/${v.chapa}`)}
                    style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{v.chapa}</span>
                    <span style={{ color: '#666' }}>{v.marca} {v.modelo}</span>
                  </div>
                ))}
              </div>
            )}
            {buscarResults.gps?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: 1, marginBottom: 4 }}>GPS</div>
                {buscarResults.gps.map(g => (
                  <div key={g.id} onClick={() => irA('/gps')}
                    style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{g.imei}</span>
                    <span style={{ color: '#666' }}>{g.nombre}</span>
                  </div>
                ))}
              </div>
            )}
            {buscarResults.ordenes?.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: 1, marginBottom: 4 }}>ÓRDENES</div>
                {buscarResults.ordenes.map(o => (
                  <div key={o.id} onClick={() => irA('/ordenes')}
                    style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontWeight: 700, color: '#3b5998' }}>#{o.id}</span>
                    <Space><Tag>{o.tipo}</Tag><Tag color={o.estado === 'COMPLETADO' ? 'success' : 'processing'}>{o.estado}</Tag></Space>
                  </div>
                ))}
              </div>
            )}
            {!buscarResults.clientes?.length && !buscarResults.vehiculos?.length && !buscarResults.gps?.length && !buscarResults.ordenes?.length && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#aaa' }}>Sin resultados para "{buscarQ}"</div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default AppLayout;
